import React, { useState } from 'react';
import {
  Box, Paper, Typography, Button, Stack, Alert, CircularProgress, TextField, Divider, Chip,
  Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import axios from 'axios';
import { apiUrl, handleApiError } from '../utils/api';

const money = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : (v ?? '');
};
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : (v ?? '');
};

const preBox = {
  bgcolor: 'grey.100', p: 2, borderRadius: 1, overflow: 'auto',
  fontFamily: 'monospace', fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
};

/** Render an extracted invoice in the original invoice layout. */
function InvoiceView({ data }) {
  const lines = Array.isArray(data.lines) ? data.lines : [];
  const invoiceFor = Array.isArray(data.invoiceFor)
    ? data.invoiceFor
    : (data.invoiceFor ? [data.invoiceFor] : []);
  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="subtitle2" color="text.secondary">INVOICE FOR:</Typography>
          {invoiceFor.map((l, i) => <Typography key={i}>{l}</Typography>)}
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h4">INVOICE</Typography>
          <Typography><strong>Number:</strong> {data.invoiceNumber || '—'}</Typography>
          <Typography><strong>Date:</strong> {data.date || '—'}</Typography>
        </Box>
      </Stack>

      <Divider sx={{ my: 2 }} />
      <Stack direction="row" spacing={6} sx={{ mb: 2 }}>
        <Typography><strong>TRUCK #:</strong> {data.truck || '—'}</Typography>
        <Typography><strong>WEEK #:</strong> {data.week ?? '—'}</Typography>
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell># OF TICKETS</TableCell>
              <TableCell>DESCRIPTION</TableCell>
              <TableCell align="right">QUANTITY</TableCell>
              <TableCell align="right">PRICE</TableCell>
              <TableCell align="right">AMOUNT</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((l, i) => (
              <TableRow key={i}>
                <TableCell>{l.tickets ?? ''}</TableCell>
                <TableCell>{l.description ?? ''}</TableCell>
                <TableCell align="right">{num(l.quantity)}</TableCell>
                <TableCell align="right">{money(l.price)}</TableCell>
                <TableCell align="right">{money(l.amount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack alignItems="flex-end" sx={{ mt: 2 }} spacing={0.5}>
        {data.amountDue != null && <Typography>AMOUNT DUE: {money(data.amountDue)}</Typography>}
        <Typography>DISCOUNT: {money(data.discount ?? 0)}</Typography>
        <Typography>SUB TOTAL: {money(data.subTotal)}</Typography>
        <Typography variant="h6">TOTAL: {money(data.total)}</Typography>
      </Stack>
    </Paper>
  );
}

/** Generic renderer for non-invoice documents: key/value table + any nested tables + full text. */
function GenericView({ data }) {
  const fields = (data.fields && typeof data.fields === 'object') ? data.fields : data;
  const scalarEntries = Object.entries(fields).filter(
    ([, v]) => v == null || typeof v !== 'object'
  );
  const arrayEntries = Object.entries(fields).filter(([, v]) => Array.isArray(v));
  const objectEntries = Object.entries(fields).filter(
    ([, v]) => v && typeof v === 'object' && !Array.isArray(v)
  );

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      {data.document_type && (
        <Typography variant="h6" gutterBottom>{data.document_type}</Typography>
      )}

      {scalarEntries.length > 0 && (
        <TableContainer sx={{ mb: 2 }}>
          <Table size="small">
            <TableBody>
              {scalarEntries.map(([k, v]) => (
                <TableRow key={k}>
                  <TableCell sx={{ fontWeight: 600, width: '35%', verticalAlign: 'top' }}>{k}</TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{String(v ?? '')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {objectEntries.map(([k, obj]) => (
        <Box key={k} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>{k}</Typography>
          <TableContainer>
            <Table size="small">
              <TableBody>
                {Object.entries(obj).map(([kk, vv]) => (
                  <TableRow key={kk}>
                    <TableCell sx={{ fontWeight: 600, width: '35%' }}>{kk}</TableCell>
                    <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{String(vv ?? '')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}

      {arrayEntries.map(([k, arr]) => {
        const cols = arr.length && typeof arr[0] === 'object'
          ? Array.from(new Set(arr.flatMap((r) => Object.keys(r)))) : null;
        return (
          <Box key={k} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>{k}</Typography>
            <TableContainer>
              <Table size="small">
                {cols ? (
                  <>
                    <TableHead>
                      <TableRow>{cols.map((c) => <TableCell key={c}>{c}</TableCell>)}</TableRow>
                    </TableHead>
                    <TableBody>
                      {arr.map((row, i) => (
                        <TableRow key={i}>
                          {cols.map((c) => <TableCell key={c}>{String(row[c] ?? '')}</TableCell>)}
                        </TableRow>
                      ))}
                    </TableBody>
                  </>
                ) : (
                  <TableBody>
                    {arr.map((v, i) => <TableRow key={i}><TableCell>{String(v)}</TableCell></TableRow>)}
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Box>
        );
      })}

      {data.full_text && (
        <>
          <Typography variant="subtitle2" gutterBottom>Full text</Typography>
          <Box component="pre" sx={preBox}>{data.full_text}</Box>
        </>
      )}
    </Paper>
  );
}

/**
 * Admin OCR tool. Upload any document (image or PDF); Gemini reads it and the result is shown
 * in a Formatted tab (invoice layout when it's an invoice, otherwise a generic field view),
 * with the raw JSON and text on their own tabs.
 */
export default function OcrTool() {
  const [file, setFile] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [tab, setTab] = useState(0);

  // Parse the structured payload (prefer backend-validated json, fall back to text).
  let parsed = null;
  if (result) {
    try { parsed = result.json ? JSON.parse(result.json) : JSON.parse(result.text); } catch { parsed = null; }
  }
  const isInvoice = parsed && (
    String(parsed.document_type || '').toLowerCase().includes('invoice') ||
    Array.isArray(parsed.lines)
  );

  const run = async () => {
    if (!file) { setError('Choose a file first.'); return; }
    setLoading(true);
    setError('');
    setResult(null);
    setTab(0);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (prompt.trim()) fd.append('prompt', prompt.trim());
      const res = await axios.post(`${apiUrl}/api/admin/ocr`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
    } catch (err) {
      setError(handleApiError(err, 'OCR failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom>Admin — Document OCR (Gemini)</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload any document (image or PDF). It is read by Gemini and shown below in its original format,
        with the JSON on a separate tab. Leave the prompt blank for the default extraction.
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
              {file ? file.name : 'Choose file'}
              <input
                type="file"
                accept="image/*,application/pdf"
                hidden
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </Button>
            <Button
              variant="contained"
              startIcon={<DocumentScannerIcon />}
              onClick={run}
              disabled={loading || !file}
            >
              {loading ? 'Running…' : 'Run OCR'}
            </Button>
          </Stack>
          <TextField
            label="Custom prompt (optional)"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            placeholder="Leave blank to auto-detect invoices, or describe the format you want."
          />
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {loading && <CircularProgress />}

      {result && (
        <Paper sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
            <Typography variant="h6">Result</Typography>
            {result.fileName && <Chip size="small" label={result.fileName} />}
            {result.mimeType && <Chip size="small" variant="outlined" label={result.mimeType} />}
            {result.model && <Chip size="small" color="primary" variant="outlined" label={result.model} />}
          </Stack>

          {result.error && <Alert severity="warning" sx={{ mb: 2 }}>{result.error}</Alert>}

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Formatted" />
            <Tab label="JSON" />
            <Tab label="Text" />
          </Tabs>

          {tab === 0 && (
            parsed
              ? (isInvoice ? <InvoiceView data={parsed} /> : <GenericView data={parsed} />)
              : <Box component="pre" sx={preBox}>{result.text || 'No structured result.'}</Box>
          )}

          {tab === 1 && (
            <Box component="pre" sx={preBox}>
              {result.json || result.text || 'No JSON returned.'}
            </Box>
          )}

          {tab === 2 && (
            <Box component="pre" sx={preBox}>
              {result.text || 'No text returned.'}
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}
